import React, { useState, useCallback } from 'react';
import { Spinner, Dropdown, ProgressBar, Toast, ToastContainer } from 'react-bootstrap';

const ExportarDatos = ({ formularios, calificaciones }) => {
  const [exporting, setExporting] = useState('');
  const [exportStatus, setExportStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const prepararDatosProveedores = useCallback(() => {
    const calcularAniosExperiencia = (fechaInicio, fechaFin) => {
      if (!fechaInicio) return 0;
      const inicio = new Date(fechaInicio);
      const fin = fechaFin ? new Date(fechaFin) : new Date();
      const diffTiempo = Math.abs(fin - inicio);
      const diffDias = diffTiempo / (1000 * 60 * 60 * 24);
      return diffDias / 360;
    };

    const obtenerCumplimiento = (calificacion) => {
      const estado = calificacion?.estado_cumplimiento || calificacion?.resultado;
      if (estado) {
        const normalizado = String(estado).toUpperCase();
        if (normalizado.includes('CUMPLE PARCIALMENTE')) return 'CUMPLE PARCIALMENTE';
        if (normalizado.includes('CUMPLE')) return 'CUMPLE';
        if (normalizado.includes('NO CUMPLE')) return 'NO CUMPLE';
        return normalizado;
      }
      const total = Number(calificacion?.total_obtenido);
      if (!Number.isNaN(total)) {
        if (total >= 75) return 'CUMPLE';
        if (total >= 50) return 'CUMPLE PARCIALMENTE';
        return 'NO CUMPLE';
      }
      return 'N/A';
    };

    return formularios
      .filter(form => form.estado === 'finalizado')
      .sort((a, b) => new Date(b.fecha_actualizacion) - new Date(a.fecha_actualizacion))
      .map((form, index) => {
        const proveedor = form.proveedor_datos || {};
        const calificacion = calificaciones.find(c => c.id_formulario === form.id_formulario)?.calificacion || {};

        let formaPago = proveedor.forma_pago || 'N/A';
        if (formaPago === 'Otro' && proveedor.forma_pago_otro) {
          formaPago = proveedor.forma_pago_otro;
        }

        const aniosExperiencia = calcularAniosExperiencia(form.fecha_inicio, form.fecha_actualizacion);
        const cumplimiento = obtenerCumplimiento(calificacion);
        const calificacionTexto = calificacion?.total_obtenido !== undefined && calificacion?.total_obtenido !== null
          ? `${Number(calificacion.total_obtenido).toFixed(2)} / 100`
          : 'N/A';

        return {
          'N°': index + 1,
          'ID Formulario': form.id_formulario || 'N/A',
          'Tipo de persona': form.usuario?.tipo_persona || 'N/A',
          'NÚMERO DE RUC': proveedor.ruc || 'N/A',
          'RAZÓN SOCIAL': proveedor.razon_social || 'N/A',
          'RAZÓN COMERCIAL': proveedor.razon_comercial || 'N/A',
          'Servicios / Suministros que presta': proveedor.servicios_prestados || 'N/A',
          'Provincia': proveedor.provincia || 'N/A',
          'Cantón': proveedor.canton || 'N/A',
          'CONTRIBUYENTE ESPECIAL': proveedor.contribuyente_especial === true ? 'SI' :
            proveedor.contribuyente_especial === false ? 'NO' : 'N/A',
          'RÉGIMEN RIMPE': proveedor.regimen_rimpe || 'N/A',
          'DIRECCIÓN DE ESTABLECIMIENTO': proveedor.direccion_establecimiento || 'N/A',
          'TELÉFONO 1': proveedor.telefono1 || 'N/A',
          'TELÉFONO 2': proveedor.telefono2 || 'N/A',
          'TELÉFONO 3': proveedor.telefono3 || 'N/A',
          'PERSONA DE CONTACTO': proveedor.persona_contacto || 'N/A',
          'CARGO': proveedor.cargo || 'N/A',
          'EMAIL DE CONTACTO': proveedor.email_contacto || 'N/A',
          'FORMA DE PAGO': formaPago,
          'EMAIL PARA TRANSFERENCIAS': proveedor.email_transferencia || 'N/A',
          'TIPO DE CUENTA': proveedor.tipo_cuenta || 'N/A',
          'NÚMERO DE CUENTA': proveedor.numero_cuenta || 'N/A',
          'NOMBRE DEL BANCO': proveedor.nombre_banco || 'N/A',
          'EMAIL PARA RETENCIONES': proveedor.email_retencion || 'N/A',
          'Representante Legal': proveedor.representante_legal || 'N/A',
          'Cédula Representante': proveedor.cedula_representante || 'N/A',
          'Calificación': calificacionTexto,
          'Años de experiencia': aniosExperiencia ? aniosExperiencia.toFixed(2) : '0.00',
          'Cumple': cumplimiento,
          'Fecha Finalización': form.fecha_actualizacion ?
            new Date(form.fecha_actualizacion).toLocaleString('es-ES') : 'N/A'
        };
      });
  }, [formularios, calificaciones]);

  const prepararDatosCalificaciones = useCallback(async () => {
    const datosBasicos = calificaciones.map((c, index) => ({
      'Número': index + 1,
      'ID Formulario': c.formulario?.id_formulario || '',
      'Formulario': c.formulario?.titulo || '',
      'Tipo de persona': c.usuario?.tipo_persona || '',
      'Proveedor': c.usuario?.nombre || '',
      'RUC': c.proveedor_datos?.ruc || '',
      'Email': c.usuario?.correo || '',
      'Calificación - Resultado Final': c.evaluacion?.resultado_final || '',
      'Observaciones de calificaciones': c.evaluacion_observaciones || '',
      'Fecha calificación': c.evaluacion?.fecha_evaluacion ?
        new Date(c.evaluacion.fecha_evaluacion).toLocaleDateString('es-ES') : ''
    }));

    return datosBasicos;
  }, [calificaciones]);

  const mostrarToast = (mensaje, variante = 'success') => {
    setToastMessage(mensaje);
    setToastVariant(variante);
    setShowToast(true);
  };

  const exportarProveedoresExcel = async () => {
    const datos = prepararDatosProveedores();

    if (datos.length === 0) {
      mostrarToast('No hay formularios finalizados para exportar', 'warning');
      return;
    }

    setExporting('proveedores');
    setExportStatus({ estado: 'preparando', registros: datos.length });
    setProgress(10);

    try {
      setExportStatus({ estado: 'enviando', registros: datos.length });
      setProgress(30);

      const response = await fetch('https://apiprov.conduto.com.ec/api/exportar/excel-proveedores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          datos: datos,
          carpetaDestino: 'Datos Proveedor'
        })
      });

      setProgress(70);

      if (response.ok) {
        const resultado = await response.json();

        setExportStatus({
          estado: 'completado',
          registros: resultado.total_registros,
          mensaje: resultado.message
        });
        setProgress(100);

        mostrarToast(`${resultado.message}\nTotal registros: ${resultado.total_registros}`, 'success');

        monitorearExportacion('proveedores', resultado.total_registros);

      } else {
        const errorData = await response.json();
        setExportStatus({ estado: 'error', mensaje: errorData.detail });
        mostrarToast(`${errorData.detail || 'Error al exportar'}`, 'danger');
      }

    } catch (error) {
      setExportStatus({ estado: 'error', mensaje: 'Error de conexión' });
      mostrarToast('Error al conectar con el servidor', 'danger');
    } finally {
      setTimeout(() => {
        setExporting('');
        setExportStatus(null);
        setProgress(0);
      }, 3000);
    }
  };

  const exportarCalificacionesExcel = async () => {
    const datos = await prepararDatosCalificaciones();

    if (datos.length === 0) {
      mostrarToast('No hay calificaciones para exportar', 'warning');
      return;
    }

    setExporting('calificaciones');
    setExportStatus({ estado: 'preparando', registros: datos.length });
    setProgress(10);

    try {
      setExportStatus({ estado: 'enviando', registros: datos.length });
      setProgress(30);

      const response = await fetch('https://apiprov.conduto.com.ec/api/exportar/excel-calificaciones', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          datos: datos,
          carpetaDestino: 'Datos Proveedor'
        })
      });

      setProgress(70);

      if (response.ok) {
        const resultado = await response.json();

        setExportStatus({
          estado: 'completado',
          registros: resultado.total_registros,
          mensaje: resultado.message
        });
        setProgress(100);

        mostrarToast(`${resultado.message}\nTotal registros: ${resultado.total_registros}`, 'success');

        monitorearExportacion('calificaciones', resultado.total_registros);

      } else {
        const errorData = await response.json();
        setExportStatus({ estado: 'error', mensaje: errorData.detail });
        mostrarToast(`${errorData.detail || 'Error al exportar'}`, 'danger');
      }

    } catch (error) {
      setExportStatus({ estado: 'error', mensaje: 'Error de conexión' });
      mostrarToast('Error al conectar con el servidor', 'danger');
    } finally {
      setTimeout(() => {
        setExporting('');
        setExportStatus(null);
        setProgress(0);
      }, 3000);
    }
  };

  const monitorearExportacion = async (tipo, totalRegistros) => {
  };

  const getStatusMessage = () => {
    if (!exportStatus) return null;

    const messages = {
      'preparando': 'Preparando datos...',
      'enviando': 'Enviando al servidor...',
      'completado': '¡Exportación completada!',
      'error': 'Error en la exportación'
    };

    return messages[exportStatus.estado] || exportStatus.mensaje;
  };

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant="success"
          disabled={exporting !== ''}
          className="d-flex align-items-center"
        >
          {exporting !== '' ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Exportando...
            </>
          ) : (
            <>
              <i className="bi bi-cloud-upload me-2"></i>
              Exportar Datos
            </>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            onClick={exportarProveedoresExcel}
            disabled={exporting !== ''}
          >
            <i className="bi bi-people me-2"></i>
            Exportar Datos de Proveedores
            <br />
            <small className="text-muted">Archivo: Datos_Proveedores.xlsx</small>
          </Dropdown.Item>

          <Dropdown.Item
            as="a"
            href="https://ecconduto.sharepoint.com/sites/CondutoEcuador/CCC%20Compras%20Control%20Y%20Contrataciones/Forms/AllItems.aspx?id=%2Fsites%2FCondutoEcuador%2FCCC%20Compras%20Control%20Y%20Contrataciones%2FVARIOS%2FCALIFICACION%20DE%20PROVEEDORES%2FDOCUMENTOS%20PROVEEDORES%2FDatos%20Proveedor&viewid=5d40ac0a%2D125d%2D4cf3%2Da0e3%2D86858db31e93"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-eye me-2"></i>
            Ver datos
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>


      {exportStatus && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="bg-white border rounded shadow-sm p-3" style={{ minWidth: '300px' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <strong>{getStatusMessage()}</strong>
                {exportStatus.registros && (
                  <div className="small text-muted">
                    Registros: {exportStatus.registros}
                  </div>
                )}
              </div>
              {exportStatus.estado === 'preparando' || exportStatus.estado === 'enviando' ? (
                <Spinner animation="border" size="sm" variant="primary" />
              ) : exportStatus.estado === 'completado' ? (
                <i className="bi bi-check-circle-fill text-success fs-4"></i>
              ) : exportStatus.estado === 'error' ? (
                <i className="bi bi-exclamation-circle-fill text-danger fs-4"></i>
              ) : null}
            </div>

            {progress > 0 && progress < 100 && (
              <ProgressBar
                now={progress}
                animated
                variant="success"
                style={{ height: '8px' }}
              />
            )}
          </div>
        </div>
      )}

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1070 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg={toastVariant}
          className="border-0"
        >
          <Toast.Body className={`d-flex align-items-center gap-2 ${toastVariant === 'success' ? 'text-white' : ''} p-3`}>
            {toastVariant === 'success' && <i className="bi bi-check-circle-fill fs-4"></i>}
            {toastVariant === 'danger' && <i className="bi bi-exclamation-circle-fill fs-4"></i>}
            {toastVariant === 'warning' && <i className="bi bi-exclamation-triangle-fill fs-4"></i>}
            <div>
              {toastMessage.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < toastMessage.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ExportarDatos;